// "Save to a list" dialog: shows the title's membership across the default
// Watchlist and every My List, lets the user toggle several, saves on Done.
// Callers must auth-gate before opening (guests never see this).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MyListsService, getMyListErrorMessage } from '../../services/myListsService';
import { getSeasonAwareTitle } from '../../utils/myListItems';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import ListNameModal from './ListNameModal';
import styles from './SaveToListModal.module.css';

const WATCHLIST_KEY = '__watchlist__';

export default function SaveToListModal({
    isOpen,
    target,          // { mediaId, mediaType, seasonNumber, title }
    source = 'unknown',
    onClose,
    onSaved,         // (membership) => void
}) {
    const [membership, setMembership] = useState(null);
    const [selected, setSelected] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        if (!target) return;
        setLoading(true);
        setError('');
        try {
            const data = await MyListsService.getMembership(target);
            const next = data?.membership;
            setMembership(next);
            const initial = { [WATCHLIST_KEY]: !!next?.watchlist?.isAdded };
            for (const list of next?.lists ?? []) initial[list.id] = !!list.isMember;
            setSelected(initial);
        } catch (err) {
            setError(getMyListErrorMessage(err, "Couldn't load your lists."));
        } finally {
            setLoading(false);
        }
    }, [target]);

    useEffect(() => {
        if (!isOpen) return undefined;
        setMembership(null);
        setSelected({});
        setError('');
        setSaving(false);
        load();
        trackEvent(ANALYTICS_EVENTS.ADD_TO_LIST_OPEN, {
            source_screen: source,
            media_type: target?.mediaType,
            season_specific: target?.seasonNumber != null,
        });
        const handleKey = (e) => { if (e.key === 'Escape' && !creating) onClose(); };
        window.addEventListener('keydown', handleKey);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', handleKey);
            document.body.style.overflow = '';
        };
    }, [isOpen, load, onClose, source, target, creating]);

    const lists = useMemo(() => membership?.lists ?? [], [membership]);

    const hasChanges = useMemo(() => {
        if (!membership) return false;
        if (selected[WATCHLIST_KEY] !== !!membership.watchlist?.isAdded) return true;
        return lists.some((list) => !!selected[list.id] !== !!list.isMember);
    }, [membership, lists, selected]);

    const atListLimit = membership?.limits
        ? (membership.limits.customListCount ?? lists.length) >= membership.limits.maxCustomListsPerUser
        : false;

    if (!isOpen) return null;

    const toggle = (key) => setSelected((prev) => ({ ...prev, [key]: !prev[key] }));

    async function handleCreate({ name, description, visibility }) {
        const data = await MyListsService.create({ name, description, visibility });
        const created = data?.list;
        trackEvent(ANALYTICS_EVENTS.MY_LIST_CREATE, { source_screen: `${source}:add_to_list`, has_description: !!description });
        if (created) {
            setMembership((prev) => ({
                ...prev,
                lists: [...(prev?.lists ?? []), { id: created.id, name: created.name, itemCount: 0, isMember: false, isFull: false }],
                limits: prev?.limits ? { ...prev.limits, customListCount: (prev.limits.customListCount ?? 0) + 1 } : prev?.limits,
            }));
            setSelected((prev) => ({ ...prev, [created.id]: true }));
        }
        setCreating(false);
    }

    async function handleDone() {
        if (!membership || saving) return;
        if (!hasChanges) { onClose(); return; }
        setSaving(true);
        setError('');
        const listIds = lists.filter((list) => selected[list.id]).map((list) => list.id);
        const watchlistChanged = selected[WATCHLIST_KEY] !== !!membership.watchlist?.isAdded;
        try {
            const data = await MyListsService.setMembership({
                mediaId: target.mediaId,
                mediaType: target.mediaType,
                seasonNumber: target.seasonNumber ?? null,
                listIds,
                ...(watchlistChanged ? { inWatchlist: !!selected[WATCHLIST_KEY] } : {}),
            });
            const next = data?.membership;
            trackEvent(ANALYTICS_EVENTS.ADD_TO_LIST_COMPLETE, {
                source_screen: source,
                media_type: target.mediaType,
                season_specific: target.seasonNumber != null,
                added_count: next?.changes?.added?.length ?? 0,
                removed_count: next?.changes?.removed?.length ?? 0,
                watchlist_changed: !!next?.changes?.watchlistChanged,
            });
            onSaved?.(next, { watchlistChanged: !!next?.changes?.watchlistChanged, inWatchlist: !!next?.watchlist?.isAdded });
            onClose();
        } catch (err) {
            setError(getMyListErrorMessage(err, "Couldn't save. Please try again."));
        } finally {
            setSaving(false);
        }
    }

    const rows = [
        { key: WATCHLIST_KEY, name: 'Watchlist', hint: 'Your queue of things to watch', isFull: false },
        ...lists.map((list) => ({
            key: list.id,
            name: list.name,
            hint: `${list.itemCount} title${list.itemCount === 1 ? '' : 's'}`,
            isFull: !!list.isFull,
        })),
    ];

    return (
        <div className={styles.backdrop} onClick={saving ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="save-to-list-title">
            <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <div>
                        <div className={styles.eyebrow}>Save to a list</div>
                        <h2 id="save-to-list-title" className={styles.title}>{getSeasonAwareTitle(target)}</h2>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose} disabled={saving} aria-label="Close">✕</button>
                </div>

                {loading ? (
                    <div className={styles.loading}>Loading your lists…</div>
                ) : (
                    <ul className={styles.list}>
                        {rows.map((row) => {
                            const checked = !!selected[row.key];
                            const disabled = row.isFull && !checked;
                            return (
                                <li key={row.key}>
                                    <label className={`${styles.row} ${disabled ? styles.rowDisabled : ''}`}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkbox}
                                            checked={checked}
                                            disabled={disabled || saving}
                                            onChange={() => toggle(row.key)}
                                        />
                                        <span className={styles.rowBody}>
                                            <span className={styles.rowTitle}>{row.name}</span>
                                            <span className={styles.rowHint}>{disabled ? 'This list is full' : row.hint}</span>
                                        </span>
                                        {row.key === WATCHLIST_KEY && <span className={styles.rowIcon} aria-hidden="true">🔖</span>}
                                    </label>
                                </li>
                            );
                        })}
                        <li>
                            <button
                                type="button"
                                className={styles.createRow}
                                onClick={() => setCreating(true)}
                                disabled={saving || atListLimit}
                            >
                                <span className={styles.createIcon} aria-hidden="true">+</span>
                                {atListLimit ? `You've reached the ${membership?.limits?.maxCustomListsPerUser}-list limit.` : 'Create New List'}
                            </button>
                        </li>
                    </ul>
                )}

                {error && <div className={styles.error} role="alert">{error}</div>}

                <button type="button" className={styles.doneBtn} onClick={handleDone} disabled={loading || saving}>
                    {saving ? 'Saving…' : hasChanges ? 'Done' : 'Close'}
                </button>
            </div>

            <ListNameModal
                isOpen={creating}
                mode="create"
                onSubmit={handleCreate}
                onClose={() => setCreating(false)}
            />
        </div>
    );
}
